// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Group, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { createReference, normalizeErrorString } from '@medplum/core';
import type { Practitioner, Reference, Task } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import { DataTable } from 'mantine-datatable';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import { TableAnchor } from '../../components/layout/TableAnchor';
import {
  type TreatmentMaintenanceEntry,
  type TreatmentMaintenanceSort,
  useTreatmentMaintenanceQueue,
} from '../../hooks/useTreatmentMaintenanceQueue';
import { withTenantScope } from '../../utils/tenant';
import { CreateTriageTaskModal, type TaskDetails } from './CreateTriageTaskModal';
import { TreatmentPatientDrawer } from './TreatmentPatientDrawer';

interface TreatmentMaintenanceTableProps {
  page: number;
  pageSize: number;
  search: string;
  onPageChange: (page: number) => void;
  onTotalChange?: (total: number) => void;
  hideTestPatients: boolean;
  sort: TreatmentMaintenanceSort;
  onSortChange: (sort: TreatmentMaintenanceSort) => void;
}

function formatDate(value: string | undefined): string {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  return Number.isFinite(date.getTime())
    ? date.toLocaleDateString(undefined, { month: '2-digit', day: '2-digit', year: 'numeric' })
    : '—';
}

function daysOnTreatment(start: string): string {
  const days = Math.max(0, Math.floor((Date.now() - new Date(start).getTime()) / 86400000));
  return `${days.toLocaleString()} days on treatment`;
}

export function TreatmentMaintenanceTable({
  page,
  pageSize,
  search,
  onPageChange,
  onTotalChange,
  hideTestPatients,
  sort,
  onSortChange,
}: TreatmentMaintenanceTableProps): JSX.Element {
  const navigate = useNavigate();
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const [taskEntry, setTaskEntry] = useState<TreatmentMaintenanceEntry | undefined>();
  const [patientEntry, setPatientEntry] = useState<TreatmentMaintenanceEntry | undefined>();
  const [submitting, setSubmitting] = useState(false);
  const { entries, loading, total } = useTreatmentMaintenanceQueue({ page, pageSize, search, hideTestPatients, sort });

  useEffect(() => onTotalChange?.(total), [onTotalChange, total]);

  const createTask = async (
    entry: TreatmentMaintenanceEntry,
    owner: Reference<Practitioner>,
    details: TaskDetails
  ): Promise<void> => {
    setSubmitting(true);
    try {
      await medplum.createResource(
        withTenantScope({
          resourceType: 'Task',
          status: 'requested',
          intent: 'order',
          priority: details.priority,
          authoredOn: new Date().toISOString(),
          restriction: details.dueDate ? [{ period: { end: `${details.dueDate}T23:59:59.000Z` } }] : undefined,
          requester: profile ? createReference(profile) : undefined,
          owner,
          for: { reference: `Patient/${entry.patientId}`, display: entry.patientName },
          code: { text: 'Treatment maintenance' },
          description: details.description,
        } as Task)
      );
      notifications.show({ color: 'green', message: 'Treatment maintenance task created' });
      setTaskEntry(undefined);
    } catch (error) {
      notifications.show({ color: 'red', message: normalizeErrorString(error) });
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    {
      accessor: 'patientName' as const,
      title: 'Patient',
      render: (entry: TreatmentMaintenanceEntry) => (
        <Stack gap={0}>
          <TableAnchor size="sm" onClick={() => setPatientEntry(entry)} style={{ cursor: 'pointer' }}>
            {entry.patientName}
          </TableAnchor>
          {entry.patientSubtitle && (
            <Text size="xs" c="dimmed">
              {entry.patientSubtitle}
            </Text>
          )}
        </Stack>
      ),
    },
    {
      accessor: 'treatmentStarted' as const,
      title: 'Treatment started',
      sortable: true,
      render: (entry: TreatmentMaintenanceEntry) => (
        <Stack gap={0}>
          <Text size="sm" fw={600}>
            {formatDate(entry.treatmentStarted)}
          </Text>
          <Text size="xs" c="dimmed">
            {daysOnTreatment(entry.treatmentStarted)}
          </Text>
        </Stack>
      ),
    },
    {
      accessor: 'modality' as const,
      title: 'Modality',
      render: (entry: TreatmentMaintenanceEntry) => (
        <Stack gap={0}>
          <Text size="sm">{entry.modality}</Text>
          {entry.modalityDetail && (
            <Text size="xs" c="dimmed">
              {entry.modalityDetail}
            </Text>
          )}
        </Stack>
      ),
    },
    {
      accessor: 'lastAppointment' as const,
      title: 'Last appointment',
      render: (entry: TreatmentMaintenanceEntry) => <Text size="sm">{formatDate(entry.lastAppointment?.date)}</Text>,
    },
    {
      accessor: 'nextAppointment' as const,
      title: 'Next appointment',
      render: (entry: TreatmentMaintenanceEntry) => (
        <Text size="sm" fw={entry.nextAppointment ? 600 : undefined}>
          {entry.nextAppointment ? formatDate(entry.nextAppointment.date) : 'Not scheduled'}
        </Text>
      ),
    },
    {
      accessor: 'nextAppointmentType' as const,
      title: 'Next appointment type',
      render: (entry: TreatmentMaintenanceEntry) => <Text size="sm">{entry.nextAppointment?.type ?? '—'}</Text>,
    },
    {
      accessor: 'actions' as const,
      title: 'Actions',
      render: (entry: TreatmentMaintenanceEntry) => (
        <Group gap="sm" wrap="nowrap">
          <TableAnchor
            size="sm"
            c="#640040"
            onClick={() => navigate(`/Patient/${entry.patientId}/Appointment/schedule`)}
            style={{ cursor: 'pointer' }}
          >
            {entry.nextAppointment ? 'Reschedule' : 'Schedule'}
          </TableAnchor>
          <TableAnchor size="sm" component="button" type="button" onClick={() => setTaskEntry(entry)}>
            Create task
          </TableAnchor>
          <TableAnchor size="sm" component="a" href="https://airview.resmed.com/" target="_blank" rel="noreferrer">
            AirView
          </TableAnchor>
        </Group>
      ),
    },
  ];

  if (!loading && total === 0) {
    return (
      <Stack gap={4} py="md">
        <Text fw={700}>No patients are currently in treatment.</Text>
        <Text size="sm" c="dimmed">
          Patients appear here as soon as a treatment episode is opened on their chart, and stay until it is ended.
          Nothing to work right now.
        </Text>
      </Stack>
    );
  }

  return (
    <>
      <DataTable
        striped
        highlightOnHover
        fetching={loading && entries.length === 0}
        records={entries}
        columns={columns}
        idAccessor="id"
        noRecordsText="No patients are currently in treatment."
        page={page}
        recordsPerPage={pageSize}
        totalRecords={total}
        onPageChange={onPageChange}
        sortStatus={{ columnAccessor: 'treatmentStarted', direction: sort }}
        onSortStatusChange={(status) => onSortChange(status.direction)}
        paginationText={({ from, to, totalRecords }) => `${from} – ${to} of ${totalRecords}`}
      />
      <CreateTriageTaskModal
        opened={Boolean(taskEntry)}
        title="Create treatment maintenance task"
        description="Review this patient's ongoing treatment maintenance needs."
        patientName={taskEntry?.patientName}
        defaultAssignee={profile?.resourceType === 'Practitioner' ? profile : undefined}
        submitting={submitting}
        onClose={() => setTaskEntry(undefined)}
        onCreate={(owner, details) => {
          if (taskEntry) {
            createTask(taskEntry, owner, details).catch(console.error);
          }
        }}
      />
      <TreatmentPatientDrawer
        patientId={patientEntry?.patientId}
        patientName={patientEntry?.patientName ?? 'Patient'}
        opened={Boolean(patientEntry)}
        onClose={() => setPatientEntry(undefined)}
      />
    </>
  );
}
